import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, Fingerprint, Loader2, Lock, Plus, Save, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rp } from "@/lib/payroll";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { useQuickProfile } from "@/context/QuickProfileContext";
import { useIsMobile } from "@/lib/useIsMobile";
import NumField from "@/components/NumField";
import ItemsDialog from "@/components/ItemsDialog";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FIELD =
  "focus-ring h-11 w-full rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3.5 text-[15px]";

const potTid = {
  dialog: "att-pot-dialog",
  preset: (k) => `att-pot-preset-${k}`,
  keterangan: (i) => `att-pot-ket-${i}`,
  nominal: (i) => `att-pot-nom-${i}`,
  remove: (i) => `att-pot-remove-${i}`,
  add: "att-pot-add",
  total: "att-pot-total",
  done: "att-pot-done",
};

const toRow = (e) => ({
  employee_id: e.employee_id,
  nama: e.nama,
  emp_code: e.emp_code || "",
  jabatan: e.jabatan,
  hari_kerja: e.hari_kerja || 0,
  kasbon: e.kasbon || 0,
  kasbon_balance: Number(e.kasbon_balance) || 0,
  barang_balance: Number(e.barang_balance) || 0,
  kasbon_cicilan: Number(e.kasbon_cicilan) || 0,
  bpjs: !!e.bpjs,
  potongan_items: (e.potongan_items || []).map((p) => ({
    keterangan: p.keterangan || "",
    nominal: Number(p.nominal) || 0,
  })),
});

const sig = (rows) => JSON.stringify(rows.map(toRow));

// Suggests a kasbon deduction from the employee's outstanding ledger balance.
const KasbonSuggest = ({ id, balance, cicilan, current, disabled, onPick }) => {
  if (disabled || !balance || balance <= 0) return null;
  const inst = cicilan > 0 ? Math.min(cicilan, balance) : 0;
  const chip = "rounded-full border px-2 py-[3px] text-[11px] font-semibold transition-all active:scale-95 disabled:opacity-40";
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`kasbon-suggest-${id}`}>
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#C0362C]">
        <Wallet className="h-3 w-3" /> Sisa Rp {rp(balance)}
      </span>
      <button type="button" data-testid={`kasbon-suggest-full-${id}`} disabled={current === balance}
        onClick={() => onPick(balance)}
        className={`${chip} border-[#C0362C]/30 bg-[#FDECEC] text-[#C0362C] hover:bg-[#FBD9D9]`}>
        Lunasi
      </button>
      {inst > 0 && inst !== balance && (
        <button type="button" data-testid={`kasbon-suggest-cicil-${id}`} disabled={current === inst}
          onClick={() => onPick(inst)}
          className={`${chip} border-[#0071E3]/30 bg-[#EAF2FE] text-[#0071E3] hover:bg-[#DCEAFE]`}>
          Cicil Rp {rp(inst)}
        </button>
      )}
    </div>
  );
};

// Shows remaining GOODS (bon barang) debt and a one-tap "Lunasi" that fills a potongan item.
const BarangSuggest = ({ id, balance, disabled, onPick }) => {
  if (disabled || !balance || balance <= 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`barang-suggest-${id}`}>
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#C0362C]">
        <Wallet className="h-3 w-3" /> Sisa Rp {rp(balance)}
      </span>
      <button type="button" data-testid={`barang-suggest-full-${id}`} onClick={() => onPick(balance)}
        className="rounded-full border border-[#C0362C]/30 bg-[#FDECEC] px-2 py-[3px] text-[11px] font-semibold text-[#C0362C] transition-all hover:bg-[#FBD9D9] active:scale-95">
        Lunasi
      </button>
    </div>
  );
};

const Header = ({ name }) => {
  const { t, months } = useT();
  return (
    <div>
      {name ? <p className="text-[13.5px] font-medium text-[#8E8E93]">{t("pay.welcome", { name })}</p> : null}
      <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">Input Payroll</h1>
    </div>
  );
};

export default function AttendancePage() {
  const { user } = useAuth();
  const { months } = useT();
  const { openQuickProfile } = useQuickProfile();
  const isAdmin = user?.role === "admin" || user?.role === "viewer";
  const readOnly = user?.role === "viewer";
  const now = new Date();
  const [view, setView] = useState("list");
  const [cards, setCards] = useState(null);
  const [pid, setPid] = useState("");
  const [pickBulan, setPickBulan] = useState(now.getMonth() + 1);
  const [pickTahun, setPickTahun] = useState(now.getFullYear());
  const [creating, setCreating] = useState(false);

  const loadCards = useCallback(async () => {
    try {
      const { data } = await api.get("/periods/attendance-summary");
      setCards(data);
    } catch (e) {
      toast.error(errMsg(e));
      setCards([]);
    }
  }, []);

  useEffect(() => {
    if (view === "list") loadCards();
  }, [view, loadCards]);

  const open = (id) => { setPid(id); setView("detail"); };

  const addMonth = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/periods/attendance", { bulan: Number(pickBulan), tahun: Number(pickTahun) });
      toast.success(`Bulan ${data.label} siap diisi`);
      open(data.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  if (view === "detail") {
    return <Detail pid={pid} userName={user?.name} isAdmin={isAdmin} readOnly={readOnly} openQuickProfile={openQuickProfile} onBack={() => setView("list")} />;
  }

  return (
    <div data-testid="attendance-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Header name={user?.name} />
        <div className="space-y-1">
          <label className="label block">Buka Bulan</label>
          <div className="flex items-end gap-2">
            <Select value={String(pickBulan)} onValueChange={(v) => setPickBulan(Number(v))}>
              <SelectTrigger data-testid="attendance-pick-bulan" className="h-10 w-32 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-[14px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <SelectItem key={m} value={String(m)}>{months[m - 1]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="number"
              data-testid="attendance-pick-tahun"
              value={pickTahun}
              onChange={(e) => setPickTahun(Number(e.target.value) || pickTahun)}
              className="h-10 w-20 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-[14px]"
            />
            {!readOnly && (
            <button
              data-testid="attendance-add-month"
              onClick={addMonth}
              disabled={creating}
              className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Buka
            </button>
            )}
          </div>
        </div>
      </div>

      {cards === null ? (
        <div className="grid place-items-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" />
        </div>
      ) : cards.length === 0 ? (
        <div className="card-soft px-6 py-20 text-center text-[14px] text-[#8E8E93]">
          Belum ada bulan. Pilih bulan lalu klik "Buka" untuk mulai mengisi absensi.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((p) => {
            const locked = !isAdmin && p.locked;
            return (
              <button
                key={p.id}
                data-testid={`attendance-card-${p.id}`}
                onClick={() => open(p.id)}
                className="card-soft group flex flex-col items-start gap-3 p-5 text-left transition-all hover:shadow-md"
              >
                <div className="flex w-full items-center justify-between">
                  <span className="text-[17px] font-semibold tracking-tight">{months[p.bulan - 1]} {p.tahun}</span>
                  {locked ? (
                    <Lock className="h-4 w-4 text-[#C79A3A]" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-[#B0B0B8] transition-transform group-hover:translate-x-0.5" />
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-[#8E8E93]">
                  <span>{p.employee_count} karyawan</span>
                  <span>·</span>
                  <span>{p.total_hari} hari kerja</span>
                </div>
                <div>
                  <p className="label">Total Kasbon</p>
                  <p className="num text-[18px] font-bold text-[#0071E3]">Rp {rp(p.total_kasbon)}</p>
                </div>
                {p.edit_requested && !p.edit_approved && !isAdmin && (
                  <span className="rounded-full bg-[#FFF8EC] px-2.5 py-1 text-[11px] font-medium text-[#8A5A00]">
                    Menunggu izin admin
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Detail({ pid, userName, isAdmin, readOnly, openQuickProfile, onBack }) {
  const { t, months } = useT();
  const [meta, setMeta] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [potFor, setPotFor] = useState(null);
  const isMobile = useIsMobile();
  const dirtyRef = useRef(dirty);
  const savingRef = useRef(saving);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);
  useEffect(() => { savingRef.current = saving; }, [saving]);

  const locked = !isAdmin && !!meta.locked;
  const fieldLock = locked || readOnly;
  const hariLocked = fieldLock || !!meta.hari_kerja_locked;

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/periods/${pid}/attendance`);
      setMeta({ is_past: data.is_past, locked: data.locked, edit_approved: data.edit_approved,
                edit_requested: data.edit_requested, label: data.label,
                bulan: data.bulan, tahun: data.tahun,
                hari_kerja_locked: data.hari_kerja_locked });
      setRows(data.entries.map(toRow));
      setDirty(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!pid) return;
    const iv = setInterval(async () => {
      if (dirtyRef.current || savingRef.current) return;
      try {
        const { data } = await api.get(`/periods/${pid}/attendance`);
        setMeta((m) => ({ ...m, locked: data.locked, edit_approved: data.edit_approved, edit_requested: data.edit_requested }));
        setRows((cur) => (sig(cur) === sig(data.entries) ? cur : data.entries.map(toRow)));
      } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [pid]);

  const setField = (employeeId, key) => (v) => {
    setRows((list) => list.map((r) => (r.employee_id === employeeId ? { ...r, [key]: v } : r)));
    setDirty(true);
  };

  const setPotongan = (employeeId) => (items) => {
    setRows((list) => list.map((r) => (r.employee_id === employeeId ? { ...r, potongan_items: items } : r)));
    setDirty(true);
  };

  // "Lunasi" for goods debt: set/replace a single "Bon barang" potongan item with the remaining amount.
  const lunasiBarang = (employeeId) => (val) => {
    setRows((list) => list.map((r) => {
      if (r.employee_id !== employeeId) return r;
      const items = (r.potongan_items || []).filter((p) => (p.keterangan || "").toLowerCase() !== "bon barang");
      items.push({ keterangan: "Bon barang", nominal: val });
      return { ...r, potongan_items: items };
    }));
    setDirty(true);
  };

  const requestEdit = async () => {
    try {
      await api.post(`/periods/${pid}/request-edit`);
      setMeta((m) => ({ ...m, edit_requested: true }));
      toast.success("Permintaan izin edit dikirim ke admin");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/periods/${pid}/attendance`, {
        entries: rows.map((r) => ({
          employee_id: r.employee_id,
          hari_kerja: Number(r.hari_kerja) || 0,
          kasbon: Number(r.kasbon) || 0,
          bpjs: !!r.bpjs,
          potongan_items: (r.potongan_items || [])
            .filter((p) => Number(p.nominal) > 0)
            .map((p) => ({ keterangan: p.keterangan || "Potongan lainnya", nominal: Number(p.nominal) })),
        })),
      });
      setDirty(false);
      toast.success("Tersimpan");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const potRow = rows.find((r) => r.employee_id === potFor);

  return (
    <div data-testid="attendance-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          data-testid="attendance-back"
          onClick={onBack}
          className="text-[13.5px] font-medium text-[#6E6E73] transition-colors hover:text-[#1D1D1F]"
        >
          ← {t("pay.allMonths").replace(/^←\s*/, "")}
        </button>
        {!readOnly && (
        <button
          data-testid="attendance-save-button"
          onClick={save}
          disabled={saving || locked || !dirty}
          className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-5 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98] disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("pay.save")}
        </button>
        )}
      </div>
      <Header name={userName} />

      {meta.label && (
        <div className="flex h-11 w-full max-w-xs items-center rounded-xl border border-[#E1E1E6] bg-[#F2F2F7] px-3.5 text-[15px] font-medium text-[#6E6E73]">
          {meta.bulan ? `${months[meta.bulan - 1]} ${meta.tahun}` : meta.label}
        </div>
      )}

      {locked && (
        <div data-testid="attendance-locked-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F5D9A8]/70 bg-[#FFF8EC]/70 px-4 py-3 backdrop-blur-xl backdrop-saturate-150">
          <p className="flex items-center gap-2 text-[13.5px] font-medium text-[#8A5A00]">
            <Lock className="h-4 w-4" /> {t("pay.lockedBanner", { label: meta.bulan ? `${months[meta.bulan - 1]} ${meta.tahun}` : meta.label })}
          </p>
          {meta.edit_requested ? (
            <span className="text-[13px] font-medium text-[#8A5A00]">{t("pay.waitingApproval")}</span>
          ) : readOnly ? null : (
            <button
              data-testid="attendance-request-edit"
              onClick={requestEdit}
              className="flex h-9 items-center gap-1.5 rounded-full bg-[#8A5A00] px-4 text-[13px] font-medium text-white transition-all hover:opacity-90"
            >
              {t("pay.requestEdit")}
            </button>
          )}
        </div>
      )}

      {meta.hari_kerja_locked && (
        <div data-testid="attendance-fingerspot-note" className="flex items-center gap-2 rounded-2xl border border-[#BFE3C8]/70 bg-[#EAF8EF]/70 px-4 py-3 text-[13px] font-medium text-[#0E8A46] backdrop-blur-xl">
          <Fingerprint className="h-4 w-4" /> {t("pay.fingerspotNote")}
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" />
        </div>
      ) : isMobile ? (
        <div className="space-y-3">
          {rows.map((r) => {
            const potTotal = (r.potongan_items || []).reduce((a, p) => a + (Number(p.nominal) || 0), 0);
            return (
              <div key={r.employee_id} data-testid={`attendance-row-${r.employee_id}`} className="card-soft px-4 py-3.5">
                <button type="button" data-testid={`attendance-name-${r.employee_id}`} onClick={() => openQuickProfile(r.employee_id)} className="text-left text-[15px] font-semibold transition-colors hover:text-[#0071E3]">{r.nama}</button>
                <p className="num text-[12px] font-medium text-[#0071E3]">{r.emp_code || "\u2014"}</p>
                <p className="text-[12px] text-[#8E8E93]">{r.jabatan || "\u2014"}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
                  <div className="flex flex-col gap-1">
                    <span className="label flex items-center gap-1">Hari Kerja{meta.hari_kerja_locked && <Fingerprint className="h-3 w-3 text-[#0E8A46]" />}</span>
                    <NumField testid={`attendance-hari-${r.employee_id}`} value={r.hari_kerja} decimal align="text-left" disabled={hariLocked} onChange={setField(r.employee_id, "hari_kerja")} className="h-10 w-full" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="label">Kasbon</span>
                    <NumField testid={`attendance-kasbon-${r.employee_id}`} value={r.kasbon} align="text-left" disabled={fieldLock} onChange={setField(r.employee_id, "kasbon")} className="h-10 w-full" />
                    <KasbonSuggest id={r.employee_id} balance={r.kasbon_balance} cicilan={r.kasbon_cicilan} current={Number(r.kasbon) || 0} disabled={fieldLock} onPick={setField(r.employee_id, "kasbon")} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="label">BPJS</span>
                    <div className="flex h-10 items-center gap-2">
                      <Switch data-testid={`attendance-bpjs-${r.employee_id}`} checked={!!r.bpjs} disabled={fieldLock} onCheckedChange={setField(r.employee_id, "bpjs")} />
                      <span className="text-[13px] text-[#8E8E93]">{r.bpjs ? "Ya" : "Tidak"}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="label">Potongan Lain</span>
                    <button data-testid={`attendance-potongan-${r.employee_id}`} onClick={() => !fieldLock && setPotFor(r.employee_id)} disabled={fieldLock} className="flex h-10 items-center justify-between gap-2 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-left text-[14px] disabled:opacity-60">
                      <span className="num">{potTotal ? `Rp ${rp(potTotal)}` : "Tambah"}</span>
                    </button>
                    <BarangSuggest id={r.employee_id} balance={r.barang_balance} disabled={fieldLock} onPick={lunasiBarang(r.employee_id)} />
                  </div>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <p className="card-soft px-4 py-14 text-center text-[14px] text-[#8E8E93]">Belum ada karyawan pada periode ini</p>
          )}
        </div>
      ) : (
        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead>
                <tr className="border-b border-black/[0.06]">
                  {["Karyawan", "Hari Kerja", "Kasbon", "BPJS", "Potongan Lain"].map((h) => (
                    <th key={h} className="label whitespace-nowrap px-4 py-3 text-left">
                      {h === "Hari Kerja" && meta.hari_kerja_locked ? (
                        <span className="flex items-center gap-1">Hari Kerja <Fingerprint className="h-3 w-3 text-[#0E8A46]" /></span>
                      ) : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const potTotal = (r.potongan_items || []).reduce((a, p) => a + (Number(p.nominal) || 0), 0);
                  return (
                    <tr
                      key={r.employee_id}
                      data-testid={`attendance-row-${r.employee_id}`}
                      className="border-b border-black/[0.04] transition-colors last:border-0 hover:bg-[#FAFAFA]"
                    >
                      <td className="px-4 py-2.5">
                        <button type="button" data-testid={`attendance-name-${r.employee_id}`} onClick={() => openQuickProfile(r.employee_id)} className="text-left text-[14.5px] font-medium transition-colors hover:text-[#0071E3]">{r.nama}</button>
                        <p className="num text-[11.5px] font-medium text-[#0071E3]">{r.emp_code || "\u2014"}</p>
                        <p className="text-[12px] text-[#8E8E93]">{r.jabatan || "\u2014"}</p>
                      </td>
                      <td className="px-3 py-2.5">
                        <NumField
                          testid={`attendance-hari-${r.employee_id}`}
                          value={r.hari_kerja}
                          decimal
                          align="text-left"
                          disabled={hariLocked}
                          onChange={setField(r.employee_id, "hari_kerja")}
                          className="h-10 w-24"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <NumField
                          testid={`attendance-kasbon-${r.employee_id}`}
                          value={r.kasbon}
                          align="text-left"
                          disabled={fieldLock}
                          onChange={setField(r.employee_id, "kasbon")}
                          className="h-10 w-32"
                        />
                        <KasbonSuggest id={r.employee_id} balance={r.kasbon_balance} cicilan={r.kasbon_cicilan} current={Number(r.kasbon) || 0} disabled={fieldLock} onPick={setField(r.employee_id, "kasbon")} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Switch
                            data-testid={`attendance-bpjs-${r.employee_id}`}
                            checked={!!r.bpjs}
                            disabled={fieldLock}
                            onCheckedChange={setField(r.employee_id, "bpjs")}
                          />
                          <span className="text-[13px] text-[#8E8E93]">{r.bpjs ? "Ya" : "Tidak"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <button
                          data-testid={`attendance-potongan-${r.employee_id}`}
                          onClick={() => !fieldLock && setPotFor(r.employee_id)}
                          disabled={fieldLock}
                          className="flex h-10 min-w-[9rem] items-center justify-between gap-2 rounded-xl border border-[#E1E1E6] bg-[#FAFAFA] px-3 text-left text-[14px] transition-colors hover:bg-[#F2F2F7] disabled:opacity-60"
                        >
                          <span className="num">{potTotal ? `Rp ${rp(potTotal)}` : "Tambah"}</span>
                          <span className="text-[11px] text-[#8E8E93]">
                            {(r.potongan_items || []).filter((p) => Number(p.nominal) > 0).length || ""}
                          </span>
                        </button>
                        <BarangSuggest id={r.employee_id} balance={r.barang_balance} disabled={fieldLock} onPick={lunasiBarang(r.employee_id)} />
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-16 text-center text-[14px] text-[#8E8E93]">
                      Belum ada karyawan pada periode ini
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ItemsDialog
        open={!!potFor}
        onOpenChange={(v) => !v && setPotFor(null)}
        title={potRow ? `Potongan Lain — ${potRow.nama}` : "Potongan Lain"}
        presets={[{ key: "titip", label: "Titip" }, { key: "baju", label: "Potong baju" }]}
        items={potRow?.potongan_items || []}
        rowKey={potFor}
        onChange={potFor ? setPotongan(potFor) : () => {}}
        tid={potTid}
      />
    </div>
  );
}
